import { z } from 'zod'

export const timeSeriesSchema = z.object({
  date: z.string(),
  count: z.number(),
})

export type TimeSeries = z.infer<typeof timeSeriesSchema>

const timeSavedItemSchema = z.object({
  total: z.number(),
  automated: z.number(),
})

export const flowSchema = z.object({
  name: z.string(),
  page_id: z.string(),
  path: z.string(),
  ns: z.string(),
})

const leadCollectorPhoneEmailSchema = z.object({
  date: z.string(),
  phones: z.number(),
  emails: z.number(),
})

export type LeadCollectorPhoneEmailType = z.infer<typeof leadCollectorPhoneEmailSchema>

const flowSimpleSchema = z.object({
  name: z.string(),
  path: z.string(),
})

const leadCollectorItemSchema = z.object({
  flow: flowSimpleSchema,
  data: z.array(timeSeriesSchema),
  automated: z.number(),
  total: z.number(),
})

const popularLinkSchema = z.object({
  total: z.number(),
  opened: z.number(),
  url: z.string(),
  automations_count: z.number(),
})

const mostEngagedFollowersSchema = z.object({
  data: z
    .object({
      count: z.number(),
      avatars: z.array(z.string().nullable()),
    })
    .nullable(),
})

export type MostEngagedFollowersDataType = z.infer<typeof mostEngagedFollowersSchema>

export enum SegmentType {
  AFFILIATE = 'affiliate',
  INFOPRENEUR = 'infopreneur',
  ECOMMERCE = 'ecommerce',
  LOCAL_BUSINESS_OR_PLACE = 'local business or place',
  OTHER = 'other',
  UNKNOWN = 'unknown',
}

const basicSchema = z.object({
  top_lead_collector: z.object({
    flow: flowSchema.nullish(),
    data: z.array(timeSeriesSchema),
  }),
  top_lead_collectors: z.object({
    data: z
      .object({
        any: z.array(leadCollectorItemSchema.nullish()).nullish(),
        emails: z.array(leadCollectorItemSchema.nullish()).nullish(),
        phones: z.array(leadCollectorItemSchema.nullish()).nullish(),
      })
      .nullish(),
  }),
  lead_collector: z.object({
    data: z.array(timeSeriesSchema),
  }),
  lead_collectors: z.object({
    data: z.array(leadCollectorPhoneEmailSchema),
  }),
  time_saved: z.object({
    data: z.array(timeSeriesSchema),
  }),
  time_saved_detailed: z.object({
    data: z
      .object({
        dm: timeSavedItemSchema.nullish(),
        comment_reply: timeSavedItemSchema.nullish(),
        story_reply: timeSavedItemSchema.nullish(),
      })
      .nullable(),
  }),
  most_engaged_followers: mostEngagedFollowersSchema,
  updated_at: z.string().nullish(),
})

const infopreneurSchema = basicSchema.extend({
  type: z.literal(SegmentType.INFOPRENEUR),
})

const ecommerceSchema = basicSchema.extend({
  type: z.literal(SegmentType.ECOMMERCE),
})

const localBusinessSchema = basicSchema.extend({
  type: z.literal(SegmentType.LOCAL_BUSINESS_OR_PLACE),
})

const otherSchema = basicSchema.extend({
  type: z.literal(SegmentType.OTHER),
})

const unknownSchema = basicSchema.extend({
  type: z.literal(SegmentType.UNKNOWN),
})

const affiliateSchema = z.object({
  type: z.literal(SegmentType.AFFILIATE),
  popular_link: z.object({
    url: z.string().nullish(),
    data: z.array(timeSeriesSchema),
  }),
  popular_links: z.object({
    data: z.array(popularLinkSchema),
  }),
  links_clicks: z.object({
    data: z.array(timeSeriesSchema),
  }),
  time_saved: z.object({
    data: z.array(timeSeriesSchema),
  }),
  time_saved_detailed: z.object({
    data: z
      .object({
        dm: timeSavedItemSchema.nullish(),
        comment_reply: timeSavedItemSchema.nullish(),
        story_reply: timeSavedItemSchema.nullish(),
      })
      .nullable(),
  }),
  most_engaged_followers: mostEngagedFollowersSchema,
  updated_at: z.string().nullish(),
})

export const segmentInsightsSchemas = z
  .discriminatedUnion('type', [
    infopreneurSchema,
    ecommerceSchema,
    localBusinessSchema,
    otherSchema,
    unknownSchema,
    affiliateSchema,
  ])
  .nullish()

export type SegmentInsightsSchemaType = z.infer<typeof segmentInsightsSchemas>

export type LeadCollectorItemSchemaType = z.infer<typeof leadCollectorItemSchema>

export type PopularLinkSchemaType = z.infer<typeof popularLinkSchema>

export type SegmentInsightsBasicSchemaType = Extract<
  z.infer<typeof segmentInsightsSchemas>,
  {
    type:
      | SegmentType.INFOPRENEUR
      | SegmentType.ECOMMERCE
      | SegmentType.LOCAL_BUSINESS_OR_PLACE
      | SegmentType.OTHER
      | SegmentType.UNKNOWN
  }
>

export type SegmentInsightsAffiliateSchemaType = Extract<
  z.infer<typeof segmentInsightsSchemas>,
  { type: SegmentType.AFFILIATE }
>
